import React, { useEffect, useState } from 'react';
import { useLang } from '../LanguageContext';
import StrainCard from '../components/StrainCard';
import DisplayModeToggle from '../components/DisplayModeToggle';
import { authFetch } from '../utils/authFetch';

import { LABEL_MODES, useLabelMode, getLabelText } from '../hooks/useLabelMode';
export default function StrainsPage() {
  const { t } = useLang();
  const token = localStorage.getItem('token');
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const isAdmin = role === 'admin' || role === 'superuser';
  const [message, setMessage] = useState('');
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [labelMode, setLabelMode] = useLabelMode();
  const [mode, setMode] = useState('medium');

  const fetchStrains = () => {
    setLoading(true);
    setLoadError('');
    const endpoint = isAdmin ? '/api/admin/strains' : '/api/strains';
    const options = {};
    authFetch(endpoint, options, { token })
      .then((r) => {
        if (!r.ok) throw new Error('load');
        return r.json();
      })
      .then((d) => setStrains(Array.isArray(d) ? d : []))
      .catch(() => {
        if (!isAdmin) setLoadError(t('strainsLoadError'));
      })
      .finally(() => setLoading(false));

    if (!isAdmin) return;

    authFetch('/api/admin/strains', {}, { token })
      .then((r) => {
        if (!r.ok) throw new Error('admin');
        return r.json();
      })
      .then((d) => {
        if (Array.isArray(d)) setStrains(d);
      })
      .catch(() => {
        setLoadError((prev) => prev || t('strainsLoadError'));
      });
  };

  useEffect(() => {
    fetchStrains();
  }, [isAdmin, token]);

  const handleAdd = async () => {
    const name = window.prompt(t('strain'));
    const slug = window.prompt(t('slug'));
    if (!name || !slug) return;
    try {
      const res = await fetch('/api/admin/strains', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, slug }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessage(err.error || 'Error');
      } else {
        setMessage('Strain added');
        fetchStrains();
      }
    } catch {
      setMessage('Error');
    }
  };

  const handleEdit = async (s) => {
    const name = window.prompt(t('strain'), s.name);
    const slug = window.prompt(t('slug'), s.slug);
    if (!name || !slug) return;
    try {
      const res = await fetch(`/api/admin/strains/${s._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          slug,
          genetics: s.genetics || '',
          description: s.description || '',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessage(err.error || 'Error');
      } else {
        setMessage('Strain updated');
        fetchStrains();
      }
    } catch {
      setMessage('Error');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      const res = await fetch(`/api/admin/strains/${s._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessage(err.error || 'Error');
      } else {
        setMessage('Strain deleted');
        fetchStrains();
      }
    } catch {
      setMessage('Error');
    }
  };

  const filtered = strains
    .filter(
      (s) =>
        s.name?.toLowerCase().includes(search.toLowerCase()) ||
        s.slug?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      return (a.slug || '').localeCompare(b.slug || '');
    });


  if (loading) {
    return (
      <div className="container mt-3">
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mt-3">
      <h1 className="mb-3">{t('nav.strains')}</h1>
      {loadError && (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      )}
      {isAdmin && (
        <div className="mb-3">
          <button className="btn btn-success me-2" onClick={handleAdd}>➕ {t('addStrain')}</button>
          {message && <div className="alert alert-info mt-2 mb-0">{message}</div>}
        </div>
      )}
      <div className="d-flex mb-3 align-items-center">
        <input
          className="form-control me-2"
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select w-auto me-2"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="name">{t('byName')}</option>
          <option value="code">{t('byCode')}</option>
        </select>
        <select
          className="form-select w-auto me-2"
          value={labelMode}
          onChange={(e) => setLabelMode(e.target.value)}
        >
          <option value={LABEL_MODES.NAME_ONLY}>{t('labelModes.nameOnly')}</option>
          <option value={LABEL_MODES.NUMBER_ONLY}>{t('labelModes.numberOnly')}</option>
          <option value={LABEL_MODES.NAME_AND_NUMBER}>{t('labelModes.nameAndNumber')}</option>
        </select>
        <DisplayModeToggle mode={mode} setMode={setMode} />
      </div>
      {mode === 'table' ? (
        <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>{t('strain')}</th>
              <th>{t('id')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s._id || s.slug}>
                <td>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ maxWidth: 220, wordBreak: 'break-word' }}
                    title={getLabelText(labelMode, s.name, s.slug || s._id, ' ')}
                  >
                    {getLabelText(labelMode, s.name, s.slug || s._id, ' ')}
                  </span>
                </td>
                <td>{s.slug || s._id}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="row">
          {filtered.map((s) => (
            <div key={s._id || s.slug} className={`${mode === 'large' ? 'mb-4' : 'mb-3'} col-12 col-md-6 col-lg-4`}>
              <StrainCard
                strain={s}
                labelMode={labelMode}
                isAdmin={isAdmin}
                publicView={!isAdmin}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>
          ))}
          {!filtered.length && !loading && (
            <div className="col-12">
              <div className="alert alert-secondary" role="status">
                {t('noStrains')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
